import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { RaportHeader } from '@/components/Raport/RaportHeader';
import { usePagesCount } from '@/hooks/usePagesCount';
import carImage from '@/assets/car.jpg';
import '@/styles/raport.css';
import { RaportShortcutSummary } from '@/components/Raport/RaportShortcutSummary/RaportShortcutSummary';
import { RaportMainParams } from '@/components/Raport/RaportMainParams/RaportMainParams';
import RaportCarBody from '@/components/Raport/RaportCarBody/RaportCarBody';
import RaportCarInner from '@/components/Raport/RaportCarInner/RaportCarInner';
import RaportTestDrive from '@/components/Raport/RaportTestDrive/RaportTestDrive';
import RaportEngine from '@/components/Raport/RaportEngine/RaportEngine';
import RaportCarBottom from '@/components/Raport/RaportCarBottom/RaportCarBottom';
import { LayoutSection } from '@/components/Layout/LayoutSection';
import { ReportGallerySections } from '@/components/ReportGallerySections';
import { RaportDiagnosticComputer } from '@/components/Raport/RaportDiagnosticComputer';
import { RaportRepairHistory } from '@/components/Raport/RaportRepairHistory';
import { RaportCepik } from '@/components/Raport/RaportCepik';
import { reportService } from '@/api/services/report.service';
import type { MappedPublicReport, AvailableSection } from '@/api/mappers/public-report.mapper';
import { generateReportMenuItems } from '@/utils/reportMenuGenerator';
import logoImg from '@/assets/logo.png';
import { prepareReportV2Data } from '@/api/mappers/hsp/prepareReportV2';
import { HSPLegal } from './componentsHSP/HSPLegal';
import { formatDate } from '@/helpers/helpers';
import { HSPEstimation } from './componentsHSP/HSPEstimation';
import { HSPMileageChart } from './componentsHSP/HSPMileageChart';
import { HSPVehicleIdentification } from './componentsHSP/HSPVehicleIdentification';
// import { HSPRecommendationsWarnings } from './componentsHSP/HSPRecommendationsWarnings';
import { HSPTimeline } from './componentsHSP/HSPTimeline';
import { HSPHistory } from './componentsHSP/HSPHistory';
// import { HSPSummary } from './componentsHSP/HSPSummary';
// import { ApiResponseRaportV2 } from '@/api/mappers/hsp/api';
import { HSPSpecs } from './componentsHSP/HSPSpecs';
import { SectionSubtitle } from '@/components';
import { HSPEquipment } from './componentsHSP/HSPEquipment';
import { HSPFinancialSpecs } from './componentsHSP/HSPFinancialSpecs';
import { Layout } from './componentsHSP/Layout';
import { SidebarAccordion, SidebarSection } from './componentsHSP/SidebarAccordion/SidebarAccordion';
import { VideoPlayer } from '@/components/VideoPlayer';
// import { HSPHistory } from './componentsHSP/HSPHistory';
// import { PinInput } from '@/components/PinInput';

export const RaportDetails = () => {
  const { id } = useParams<{ id: string }>();
  const [reportData, setReportData] = useState<MappedPublicReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  // const [pinEntered, setPinEntered] = useState(false);
  // const [email, setEmail] = useState<string | null>(null);
  // const [reportId, setReportId] = useState<string | null>(null);

  // Ref do głównego kontenera zawartości raportu
  const reportContentRef = useRef<HTMLDivElement>(null);

  // Oblicz liczbę stron na podstawie wysokości zawartości
  const calculatedPagesCount = usePagesCount(reportContentRef, [reportData]);

  // Generate menu items based on report data
  const menuItems = useMemo(() => {
    return generateReportMenuItems(reportData);
  }, [reportData]);

  const reportV2Data = useMemo(() => {
    if (!reportData?.hspSection) {
      return null;
    }

    // Lekka pozycja statusowa dodatku HSP: NOT_FOUND = pojazd sprawdzony,
    // brak odnotowanych szkód → pokazujemy kartę "Nie wykazano historii szkodowej".
    const historyNoDamage = reportData.addons.some(
      (addon) =>
        addon.type === "HSP_VEHICLE_HISTORY" && addon.status === "NOT_FOUND"
    );

    const { header, sections, sidebarItems } = prepareReportV2Data(
      reportData.hspSection,
      undefined,
      { historyNoDamage }
    );
    return { header, sections, sidebarItems };
  }, [reportData]);

  // Tworzenie sekcji dla SidebarAccordion
  const sidebarSections = useMemo<SidebarSection[]>(() => {
    const sections: SidebarSection[] = [];

    // Sekcja główna - Raport z oględzin
    if (menuItems.length > 0) {
      sections.push({
        id: 'report-main',
        title: 'Raport z oględzin',
        items: menuItems,
      });
    }

    // Sekcja dodatkowa - Raporty dodatkowe (z HSP)
    if (reportV2Data?.sidebarItems && reportV2Data.sidebarItems.length > 0) {
      sections.push({
        id: 'report-additional',
        title: 'Raporty dodatkowe',
        items: reportV2Data.sidebarItems,
      });
    }

    return sections;
  }, [menuItems, reportV2Data?.sidebarItems]);

  useEffect(() => {
    const fetchReport = async () => {
      if (!id) {
        setError('Brak ID raportu w URL');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await reportService.getPublicReport(id);

        if (response.data) {
          setReportData(response.data);
        } else {
          setError('Brak danych w odpowiedzi z serwera');
        }
      } catch (err) {
        console.error('Błąd podczas pobierania raportu publicznego:', err);
        const errorMessage = err instanceof Error
          ? err.message
          : 'Nie udało się pobrać danych raportu';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [id]);

  // const handlePinSubmit = async (pin: string) => {
  //   if (!id || !email || !reportId) return;

  //   try {
  //     setLoading(true);
  //     setError(null);

  //     // Step 1: Get access token
  //     // id = linkId, reportId = reportId
  //     // const tokenResponse = await reportService.getAccessToken(
  //     //   id,
  //     //   reportId,
  //     //   email,
  //     //   pin,
  //     // );

  //     // if (!tokenResponse.data.accessToken) {
  //     //   setError('Nie udało się uzyskać tokenu dostępowego');
  //     //   return;
  //     // }

  //     // Step 2: Get report with access token
  //     const reportResponse = await reportService.getPublicReport(
  //       reportId,
  //       id,
  //       // tokenResponse.data.accessToken,
  //     );
  //     setReportData(reportResponse.data);
  //     setPinEntered(true);
  //   } catch (err) {
  //     console.error('Błąd podczas pobierania raportu publicznego:', err);
  //     setError((err as Error).message || 'Nieprawidłowy PIN lub wystąpił błąd');
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Show PIN input if PIN hasn't been entered yet
  // if (!pinEntered) {
  //   return <PinInput onSubmit={handlePinSubmit} loading={loading} error={error} />;
  // }

  const handleDownloadPdf = useCallback(() => {
    setPdfLoading(true);

    // 1. Wyciągnij WSZYSTKIE responsive reguły CSS i wstrzyknij jako @media print
    // — Chrome nie triggeruje breakpointów w druku (viewport ~794px)
    const styleEl = document.createElement('style');
    styleEl.id = 'print-force-desktop';
    let printCss = '@media print {\n';

    for (const sheet of document.styleSheets) {
      try {
        for (const rule of sheet.cssRules) {
          if (rule instanceof CSSMediaRule) {
            const match = rule.media.mediaText.match(/min-width:\s*(\d+)/);
            if (match) {
              for (const innerRule of rule.cssRules) {
                printCss += innerRule.cssText + '\n';
              }
            }
          }
        }
      } catch {
        // Cross-origin stylesheet — ignoruj
      }
    }

    printCss += '}';
    styleEl.textContent = printCss;
    document.head.appendChild(styleEl);

    // 2. Bezpośrednia manipulacja DOM — ukryj sidebar i usuń offset
    const savedInlineStyles: Array<{ el: HTMLElement; cssText: string }> = [];

    // Ukryj sidebar i inne elementy print:hidden
    document.querySelectorAll<HTMLElement>('[class]').forEach((el) => {
      if (typeof el.className !== 'string') return;
      if (el.className.includes('print:hidden')) {
        savedInlineStyles.push({ el, cssText: el.style.cssText });
        el.style.setProperty('display', 'none', 'important');
      }
      // Usuń offset sidebara z głównego contentu
      if (el.className.includes('desktop:ml-')) {
        savedInlineStyles.push({ el, cssText: el.style.cssText });
        el.style.setProperty('margin-left', '0', 'important');
        el.style.setProperty('width', '100%', 'important');
      }
    });

    const cleanup = () => {
      styleEl.remove();
      savedInlineStyles.forEach(({ el, cssText }) => {
        el.style.cssText = cssText;
      });
      setPdfLoading(false);
      window.removeEventListener('afterprint', cleanup);
    };

    window.addEventListener('afterprint', cleanup);
    window.print();
  }, []);

  const handleDownloadImages = () => {
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center text-gray-600">
          <div className="mb-4">Ładowanie danych raportu...</div>
          <div className="text-sm text-gray-400">ID: {id}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="mb-2 font-semibold text-red-600">Błąd podczas pobierania raportu</div>
          <div className="mb-2 text-gray-600">{error}</div>
          <div className="text-sm text-gray-400">ID: {id}</div>
        </div>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center text-gray-600">
          Nie udało się pobrać danych raportu
        </div>
      </div>
    );
  }

  const carBasicInfo = reportData.carBasicInfo;
  const reportSummary = reportData.summary;
  // Dane dla RaportMainParams
  const vehicleMainData = {
    brand: carBasicInfo.vehicleBrand,
    model: carBasicInfo.vehicleModel,
    productionYear: carBasicInfo.yearOfProduction || '',
    vin: carBasicInfo.vinNumber,
    mileage: carBasicInfo.mileage || '',
    countryOfOrigin: carBasicInfo.countryOfOrigin || '',
    fuelType: carBasicInfo.fuelType || '',
    engineDisplacement: carBasicInfo.engineDisplacement || '',
    enginePower: carBasicInfo.enginePower || '',
    driveType: carBasicInfo.driveType || '',
    engineLayout: carBasicInfo.engineLayout || '',
    transmission: carBasicInfo.transmission || '',
    bodyType: carBasicInfo.bodyType || '',
    doorCount: carBasicInfo.doorCount || '',
    color: carBasicInfo.color || '',
    maxWeight: carBasicInfo.maxWeight || '',
    registrationNumber: carBasicInfo.registrationNumber,
    insuranceExpiryDate: carBasicInfo.insuranceExpiryDate,
    firstRegistrationDate: carBasicInfo.firstRegistrationDate,
    nextTechnicalInspectionDate: carBasicInfo.nextTechnicalInspectionDate,
    firstRegistrationInPoland: carBasicInfo.firstRegistrationInPoland,
    isRegistrationNumberConsistent: carBasicInfo.isRegistrationNumberConsistent,
  };

  const has = (s: AvailableSection) => reportData.availableSections.has(s);

  // Widok pełny - raport zweryfikowany
  return (
    <Layout
      title="Twój raport z ekspertyzy"
      hasSidebar={true}
      logo={<img src={logoImg} alt="Logo" className="w-full" />}
      navbarContent={<>
        <div className="flex w-full max-w-[1348px] flex-col-reverse items-center gap-6 py-6 lg:flex-row lg:justify-end lg:gap-6 lg:px-8 print:hidden">
          <div className="flex flex-col gap-2 justify-center items-center lg:items-end lg:justify-end">
            <h1 className="text-[22px] font-bold text-text-primary lg:text-right lg:text-[26px]">
              Twój raport z ekspertyzy
            </h1>
          </div>
          <div className="hidden lg:block w-[1px] h-[50px] bg-gray-200"></div>
          <img src={logoImg} alt="Logo" className="h-[52px] w-auto" />
        </div>
      </>}
      sidebarContent={<SidebarAccordion sections={sidebarSections} />}
      footerContent={<></>}
    >
      <div ref={reportContentRef}>
        <div className="raport-bg raport-wrapper p-4 lg:pl-[48px]">
          <RaportHeader
            vehicleBrand={carBasicInfo.vehicleBrand}
            vehicleModel={carBasicInfo.vehicleModel}
            vinNumber={carBasicInfo.vinNumber}
            orderNumber={reportData?.externalOrderId || ''}
            packageType={reportData?.reportType || ''}
            reportDate={formatDate(reportData?.createdAt || '') || ''}
            pagesCount={calculatedPagesCount}
            vehicleType={carBasicInfo.vehicleType}
            logoUrl={carBasicInfo.logoUrl || ''}
            carImageUrl={reportData.galleries.mainPhotoUrl || carBasicInfo.carImageUrl || carImage}
            onDownloadPdf={handleDownloadPdf}
            pdfLoading={pdfLoading}
            photoCount={reportData.galleries.all.length}
            onDownloadImages={handleDownloadImages}
          />
        </div>

        <div className="ml-0 bg-white">
          {has('REPORT_SUMMARY') && reportSummary && (
            <div className="relative">
              <div id="skrot-raportu" className="absolute -top-[500px]" />
              <LayoutSection title="" description="" icon="paper">
                <RaportShortcutSummary
                  summaryCards={reportSummary.summaryCards}
                  overallRating={reportSummary.overallRating}
                  expertWouldBuy={reportSummary.expertWouldBuy}
                  inspectionSummary={reportSummary.inspectionSummary}
                  mileageVerificationDescription={
                    reportSummary.mileageVerificationDescription
                  }
                  mileageVerificationIsPositive={
                    reportSummary.mileageVerificationIsPositive
                  }
                  advertisementVerificationDescription={
                    reportSummary.advertisementVerificationDescription
                  }
                  advertisementVerificationIsPositive={
                    reportSummary.advertisementVerificationIsPositive
                  }
                  sellerInterviewDescription={
                    reportSummary.sellerInterviewDescription
                  }
                  availableSections={reportData.availableSections}
                />
              </LayoutSection>
            </div>
          )}

          {has('CAR_BASIC_INFO') && (
            <LayoutSection
              title="Podstawowe informacje"
              description="Szczegółowe parametry techniczne dotyczące pojazdu."
              icon="i"
              id="podstawowe-informacje"
            >
              <RaportMainParams vehicleData={vehicleMainData} mainPhotoUrl={reportData.galleries.mainPhotoUrl || carBasicInfo.carImageUrl || carImage} />
            </LayoutSection>
          )}

          {has('EXTERNAL_INSPECTION') && reportData.carBody && (
            <div id="car-body">
              <RaportCarBody
                ratingSection={reportData.carBody.ratingSection}
                remarks={reportData.carBody.remarks}
                paintAndGapMeasurements={reportData.carBody.paintAndGapMeasurements}
                windowsData={reportData.carBody.windowsData}
                tiresData={reportData.carBody.tiresData}
                lightsData={reportData.carBody.lightsData}
                galleryPhotos={reportData.galleries.body}
                vehicleType={carBasicInfo.vehicleType}
              />
            </div>
          )}

          {has('INTERNAL_INSPECTION') && reportData.carInterior && (
            <LayoutSection
              title="Wnętrze"
              description="Stan elementów kokpitu / Stan elementów wnętrza / Wyposażenie"
              icon="trophy"
              id="car-interior"
            >
              <RaportCarInner
                ratingSection={reportData.carInterior.ratingSection}
                remarks={reportData.carInterior.remarks}
                cockpitSection={reportData.carInterior.cockpitSection}
                interiorSection={reportData.carInterior.interiorSection}
                equipmentItems={reportData.carInterior.equipmentItems}
                equipmentExpertSummary={reportData.carInterior.equipmentExpertSummary}
                galleryPhotos={reportData.galleries.interior}
              />
            </LayoutSection>
          )}

          {has('ENGINE_AND_TRUNK_COMPARTMENT') && reportData.engineAndTrunk && (
            <LayoutSection
              title="Komora silnika i bagażnika"
              description="Stan komory silnika / Stan komory bagażnika"
              icon="handshake"
              id="engine-trunk"
            >
              <RaportEngine
                ratingSection={reportData.engineAndTrunk.ratingSection}
                remarks={reportData.engineAndTrunk.remarks}
                engineCompartmentItems={reportData.engineAndTrunk.engineCompartmentItems}
                trunkCompartmentItems={reportData.engineAndTrunk.trunkCompartmentItems}
                engineExpertSummary={reportData.engineAndTrunk.engineExpertSummary}
                trunkExpertSummary={reportData.engineAndTrunk.trunkExpertSummary}
                galleryPhotos={reportData.galleries.engineCompartment}
              />
            </LayoutSection>
          )}

          {has('TEST_DRIVE') && reportData.testDrive && (
            <LayoutSection
              title="Jazda próbna"
              description="Wnioski sporządzone podczas jazdy próbnej."
              icon="steering"
              id="test-drive"
            >
              <RaportTestDrive
                ratingSection={reportData.testDrive.ratingSection}
                remarks={reportData.testDrive.remarks}
                testDriveItems={reportData.testDrive.testDriveItems}
                expertSummary={reportData.testDrive.expertSummary}
                drivenBy={reportData.testDrive.drivenBy}
              />
            </LayoutSection>
          )}

          {has('CAR_CHASSIS') && reportData.chassis && (
            <LayoutSection
              title="Podwozie"
              description="Błędy i nieprawidłowości wykryte podczas badania na kanale w warsztacie diagnostycznym."
              icon="network-nodes"
              id="chassis"
            >
              <RaportCarBottom
                ratingSection={reportData.chassis.ratingSection}
                remarks={reportData.chassis.remarks}
                chassisItems={reportData.chassis.chassisItems}
                expertSummary={reportData.chassis.expertSummary}
                galleryPhotos={reportData.galleries.undercarriage}
              />
            </LayoutSection>
          )}

          {has('COMPUTER_DIAGNOSTICS') && reportData.computerDiagnostics && (
            <LayoutSection
              title="Komputer diagnostyczny"
              description="Błędy i nieprawidłowości wykryte podczas badania komputerem diagnostycznym."
              icon="monitor-screen"
              id="computer-diagnostics"
            >
              <RaportDiagnosticComputer
                hasErrors={reportData.computerDiagnostics.hasErrors}
                readStatus={reportData.computerDiagnostics.readStatus}
                errors={reportData.computerDiagnostics.errors}
                expertSummary={reportData.computerDiagnostics.expertSummary}
                isCommunicationSuccessful={reportData.computerDiagnostics.isCommunicationSuccessful}
                galleryPhotos={reportData.galleries.diagnostics}
              />
            </LayoutSection>
          )}

          {has('REPAIR_HISTORY') && reportData.repairHistory && (
            <LayoutSection
              title="Historia napraw"
              description="Chronologiczna historia napraw pojazdu."
              icon="wrench-tool"
              id="historia-napraw"
            >
              <RaportRepairHistory
                historyItems={reportData.repairHistory.historyItems}
                expertSummary={reportData.repairHistory.expertSummary}
                galleryPhotos={reportData.galleries.documentation}
              />
            </LayoutSection>
          )}

          {/* Sekcja CEPiK */}
          {reportData.cepikSection && (
            <LayoutSection
              title="Informacje o pojeździe"
              description="Dane o pojeździe dostępne w publicznych bazach danych"
              icon="copyright"
              id="informacje-o-pojezdzie"
            >
              <RaportCepik
                technicalData={reportData.cepikSection.technicalData}
                documents={reportData.cepikSection.documents}
                homologation={reportData.cepikSection.homologation}
                eventsSummary={reportData.cepikSection.eventsSummary}
                currentStatus={reportData.cepikSection.currentStatus}
                timelineEvents={reportData.cepikSection.timelineEvents}
              />
            </LayoutSection>
          )}

          {/* Sekcja Film wideo z oględzin - videoUrl przekazywany jako props */}
          {reportData.videoUrl && (
            <LayoutSection
              title="Film wideo z oględzin"
              description="Zobacz i usłysz swoje przyszłe auto bezpośrednio na filmie."
              icon="video-camera"
              id="video"
            >
              <VideoPlayer
                videoUrl={reportData.videoUrl}
                thumbnailUrl={reportData.videoThumbnailUrl}
                vehicleDescription={`${carBasicInfo.vehicleBrand} ${carBasicInfo.vehicleModel}`}
              />
            </LayoutSection>
          )}

          {/* {reportV2Data?.sections?.summary.visible && reportV2Data.sections.summary.stats && (
            <LayoutSection
              title=""
              description={
                "Ogólne informacje o raporcie i pojezdzie"
              }
              icon="apron-clothing"
              id="hsp-podsumowanie"
            >
              <HSPSummary
                raport={reportData.hspSection as ApiResponseRaportV2}
                data={reportV2Data.sections.summary.stats}
                evaluation={reportV2Data.sections.summary.evaluation}
                sections={reportV2Data.sections}
              />
            </LayoutSection>
          )} */}

          {/* {reportV2Data?.sections?.warnings?.visible && (
            <LayoutSection
              title={"Rekomendacje i ostrzeżenia"}
              description={
                "Zestawienie wykrytych problemów i weryfikacja w oparciu o stopień ryzyka zakupu."
              }
              icon="warning"
              id="rekomendacje-ostrzezenia"
            >
              <HSPRecommendationsWarnings
                data={{
                  warnings: reportV2Data?.sections?.warnings?.items,
                }}
              />
            </LayoutSection>
          )} */}

          {/* Galeria - wszystkie zdjęcia z podziałem na kategorie */}
          {reportData.galleries.all.length > 0 && (
            <ReportGallerySections galleries={reportData.galleries} />
          )}

          {reportV2Data?.sections?.identification?.visible && (
            <LayoutSection
              title={"Dane identyfikacyjne"}
              description={"Skrót informacji identyfikacyjnych pojazdu."
              }
              icon="i"
              id="dane-identyfikacyjne"
            >
              <HSPVehicleIdentification
                data={reportV2Data?.sections?.identification?.data}
                onShowFullVehicleCard={() =>
                  (window.location.href = "#dane-techniczne")
                }
              />
            </LayoutSection>
          )}

          {reportV2Data?.sections?.timeline?.visible && (
            <LayoutSection
              title={"Oś czasu pojazdu"}
              description={
                "Historyczne dane dotyczące pojazdu w formie osi czasu."
              }
              icon="fish-swimming"
              id="os-czasu"
            >
              <HSPTimeline historyData={reportV2Data?.sections?.timeline?.data} />
            </LayoutSection>
          )}
          {reportV2Data?.sections?.mileage?.visible && (
            <LayoutSection
              title={"Wykres przebiegów"}
              description={
                "Graficzna prezentacja wszystkich odnotowanych przebiegów pojazdu w formie wykresu."
              }
              icon="speedometer"
              id="historia-przebiegu"
            >
              <HSPMileageChart entries={reportV2Data?.sections?.mileage?.entries} />
            </LayoutSection>
          )}

          {reportV2Data?.sections?.history?.visible && (
            <LayoutSection
              title={"Historia szkód pojazdu"}
              description={
                "Szczegółowe parametry historii napraw i uszkodzeń."
              }
              icon="car-accident"
              id="historia-szkod"
            >
              <HSPHistory
                historyData={reportV2Data?.sections?.history?.entries}
                reportStats={reportV2Data?.sections?.summary?.stats}
                noDamage={reportV2Data?.sections?.history?.noDamage}
              />
            </LayoutSection>
          )}

          {reportV2Data?.sections?.legal?.visible && (
            <LayoutSection
              title={"Status prawny"}
              description={
                "Sprawdzenie w bazach danych krajowych i międzynarodowych"
              }
              icon="libra"
              id="status-prawny"
            >
              <HSPLegal
                pledgeRegisters={reportV2Data?.sections?.legal?.theftInfo?.pledgeRegisters}
                theftDatabases={reportV2Data?.sections?.legal?.theftInfo?.stolenDatabases}
                verificationDate={reportV2Data?.sections?.legal?.theftInfo?.checkedAt}
              />
            </LayoutSection>
          )}

          {reportV2Data?.sections?.estimation?.visible && reportV2Data?.sections?.estimation?.evaluation && (
            <LayoutSection
              title={"Wycena wartości pojazdu"}
              description={
                "Szczegóły dotyczące wartości pojazdu oraz wszystkich elementów jego wyposażenia."
              }
              icon="ruler-measure"
              id="wycena"
            >
              <HSPEstimation evaluation={reportV2Data?.sections?.estimation?.evaluation} />
            </LayoutSection>
          )}

          {reportV2Data?.sections?.equipment.visible && (
            <LayoutSection
              title={"Wyposażenie pojazdu"}
              description={"Pełne zestawienie wyposażenia pojazdu."}
              icon="trophy"
              id="wyposazenie-pojazdu"
            >
              {reportV2Data?.sections?.equipment.hasStandardDetails && (
                <div className="mt-20">
                  {reportV2Data?.sections?.equipment.hasFinancialSpecs && (
                    <div className="mt-10">
                      <HSPFinancialSpecs
                        evaluation={reportV2Data.sections.equipment.evaluation}
                        equipment={reportV2Data.sections.equipment.equipment}
                      />
                    </div>
                  )}
                  {reportV2Data?.sections?.equipment.standardTabs.length > 0 && (
                    <div className="mt-20" id="wyposazenie-standardowe">
                      <SectionSubtitle
                        title={
                          "Wyposażenie standardowe"
                        }
                        icon="text-abc"
                      />
                      <HSPEquipment
                        data={reportV2Data?.sections?.equipment.standardTabs}
                        title={
                          "Wyposażenie standardowe"
                        }
                      />
                    </div>
                  )}
                </div>
              )}
              {reportV2Data?.sections?.equipment.optionalTabs.length > 0 && (
                <div id="wyposazenie-opcjonalne" className="mt-20">
                  <SectionSubtitle
                    title={"Wyposażenie opcjonalne"}
                    icon="star-rating"
                  />
                  <HSPEquipment
                    data={reportV2Data?.sections?.equipment.optionalTabs}
                    title={"Wyposażenie opcjonalne"}
                  />
                </div>
              )}
            </LayoutSection>
          )}



          {reportV2Data?.sections?.specs.visible && reportV2Data?.sections?.specs.data && (
            <LayoutSection
              title={"Dane techniczne"}
              description={
                "Parametry techniczne i specyfikacja pojazdu"
              }
              icon="car"
              id="dane-techniczne"
            >
              <HSPSpecs
                data={reportV2Data?.sections?.specs.data}
              />
            </LayoutSection>
          )}


          <LayoutSection className='!mt-0 !lg:mt-[60px]'>
            {/* Informacje i zastrzeżenia końcowe */}
            <div className="text-sm my-4" style={{ color: '#606C77' }}>
              <p className="mb-4">Informacje dotyczące charakteru raportu</p>
              <p className="mb-4">
                Raport został opracowany z zachowaniem najwyższej staranności oraz na podstawie szczegółowo zebranych informacji.
                Dane zawarte w raporcie mają jednak charakter informacyjny i poglądowy.
              </p>
              <p className="mb-4">
                Dlaczego raport nie powinien stanowić jedynej podstawy decyzji zakupowej:{' '}
                Raport przygotowywany jest przez Rzeczoznawcę Motocontroler, którego ocena może być częściowo subiektywna
                i nie zawsze musi pokrywać się z oczekiwaniami lub indywidualną opinią klienta.
              </p>
              <p className="mb-4">
                Uwaga dotycząca zakupu zdalnego: Raport ten nie może stanowić jedynej podstawy do podejmowania
                decyzji o zakupie pojazdu bez osobistego kontaktu z samochodem („zakup zdalny"). Przed finalizacją transakcji
                bezwzględnie wymagane są osobiste oględziny pojazdu.
              </p>
              <p className="mb-3 ">
                Zalecane działania przed zakupem (których Motocontroler nie wykonuje ze względów logistycznych):
              </p>
              <div className="mb-3">
                <p className="mb-1">Weryfikacja techniczna:</p>
                <ol className="list-decimal pl-5 text-sm">
                  <li>Kontakt z Autoryzowaną Stacją Obsługi w celu:</li>
                  <li>Potwierdzenia historii eksploatacji pojazdu,</li>
                  <li>Sprawdzenia kompresji w cylindrach,</li>
                  <li>Przeprowadzenia analizy składu mieszanki spalinowej.</li>
                </ol>
              </div>
              <div>
                <p className="mb-1">Weryfikacja prawna:</p>
                <ol className="list-decimal pl-5 text-sm">
                  <li>Sprawdzenie w odpowiedniej jednostce Policji, czy pojazd nie figuruje jako utracony w wyniku kradzieży.</li>
                  <li>Weryfikacja w odpowiednich rejestrach, czy samochód nie jest objęty zastawem rejestrowym lub innymi obciążeniami prawnymi.</li>
                </ol>
              </div>
            </div>
          </LayoutSection>
        </div>
      </div>
    </Layout >
  );
};

export default RaportDetails;
